import React, { useCallback, useState, useRef } from "react";
import { Upload, X, FileText, Image, File, Download } from "lucide-react";
import { Controller } from "react-hook-form";
import { toast } from "@shared/hooks/use-toast";

interface FileUploadProps {
  name: string;
  form: any;
  field: any;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  className?: string;
  onChange?: (files: File[]) => void;
}

interface UploadedFile {
  file: File;
  id: string;
  size: string;
  type: string;
  preview?: string;
}

const FileUploadComponent: React.FC<FileUploadProps> = ({
  name,
  form,
  field,
  accept = "*/*",
  multiple = false,
  maxFiles = 10,
  maxSize = 10, // 10MB default
  disabled = false,
  placeholder = "Drag and drop files here, or browse",
  helperText,
  className = "",
  onChange,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  // Get file icon based on type
  const getFileIcon = useCallback((fileType: string, fileName?: string) => {
    const fileExtension = fileName
      ? fileName.split(".").pop()?.toLowerCase()
      : "";

    if (
      fileType.startsWith("image/") ||
      ["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(fileExtension || "")
    ) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (fileType.includes("pdf") || fileExtension === "pdf") {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else if (["doc", "docx"].includes(fileExtension || "")) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    } else {
      return <File className="w-5 h-5 text-gray-500" />;
    }
  }, []);

  // Validate file
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check max files limit
      if (uploadedFiles.length + 1 > maxFiles) {
        return `Maximum ${maxFiles} files allowed`;
      }

      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return `File size must be less than ${maxSize}MB`;
      }

      // Check file type if accept is specified
      if (accept !== "*/*") {
        const acceptedTypes = accept.split(",").map((type) => type.trim());
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
        const mimeType = file.type;

        const isValidType = acceptedTypes.some((acceptType) => {
          if (acceptType.startsWith(".")) {
            return fileExtension === acceptType.toLowerCase();
          }
          return mimeType.match(acceptType.replace("*", ".*"));
        });

        if (!isValidType) {
          return `File type not allowed. Accepted types: ${accept}`;
        }
      }

      return null;
    },
    [accept, maxSize, maxFiles, uploadedFiles.length]
  );

  // Process files
  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const newUploadedFiles: UploadedFile[] = [];
      const errors: string[] = [];

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
          continue;
        }

        const uploadedFile: UploadedFile = {
          file,
          id: Math.random().toString(36).substr(2, 9),
          size: formatFileSize(file.size),
          type: file.type,
        };

        // Create preview for images
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            uploadedFile.preview = e.target?.result as string;
          };
          reader.readAsDataURL(file);
        }

        newUploadedFiles.push(uploadedFile);
      }

      if (errors.length > 0) {
        toast({
          title: "",
          description: `File upload errors:, ${errors}`,
        });
        toast({
          title: "",
          description: `Some files could not be uploaded:\n + ${errors.join(
            "\n"
          )}`,
        });
        // Show errors to user (you can integrate with toast notifications here)
        return;
      }

      const updatedFiles = multiple
        ? [...uploadedFiles, ...newUploadedFiles]
        : newUploadedFiles;
      setUploadedFiles(updatedFiles);

      // Update form value
      const fileList = updatedFiles.map((f) => f.file);
      form.setValue(name, multiple ? fileList : fileList[0] || null);
      onChange?.(fileList);
    },
    [
      uploadedFiles,
      validateFile,
      formatFileSize,
      multiple,
      form,
      name,
      onChange,
    ]
  );

  // Handle drag events
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [disabled, processFiles]
  );

  // Handle file input change
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      // Reset input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFiles]
  );

  // Handle browse button click
  const handleBrowseClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // Remove file
  const removeFile = useCallback(
    (fileId: string) => {
      const updatedFiles = uploadedFiles.filter((f) => f.id !== fileId);
      setUploadedFiles(updatedFiles);

      // Update form value
      const fileList = updatedFiles.map((f) => f.file);
      form.setValue(name, multiple ? fileList : fileList[0] || null);
      onChange?.(fileList);
    },
    [uploadedFiles, form, name, multiple, onChange]
  );

  // Download file (for preview)
  const downloadFile = useCallback((uploadedFile: UploadedFile) => {
    const url = URL.createObjectURL(uploadedFile.file);
    const a = document.createElement("a");
    a.href = url;
    a.download = uploadedFile.file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field: controllerField }) => (
        <div className={`w-full ${className}`}>
          {/* Upload Area */}
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
              ${
                isDragOver
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-300 bg-gray-50"
              }
              ${
                disabled || uploadedFiles.length >= maxFiles
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-gray-400 cursor-pointer"
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={
              uploadedFiles.length < maxFiles ? handleBrowseClick : undefined
            }
          >
            {/* Upload Icon */}
            <div className="flex justify-center mb-4">
              <Upload
                className={`w-12 h-12 ${
                  isDragOver ? "text-blue-500" : "text-gray-400"
                }`}
              />
            </div>

            {/* Upload Text */}
            <div className="space-y-2">
              <p className="text-gray-600">
                {uploadedFiles.length >= maxFiles
                  ? `Maximum ${maxFiles} files reached`
                  : placeholder}
              </p>
              {helperText && (
                <p className="text-sm text-gray-500">{helperText}</p>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={handleFileChange}
              disabled={disabled || uploadedFiles.length >= maxFiles}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              style={{ display: "none" }}
            />
          </div>

          {/* Browse Button */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleBrowseClick}
              disabled={disabled || uploadedFiles.length >= maxFiles}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadedFiles.length >= maxFiles
                ? "Maximum Files Reached"
                : "Choose Files"}
            </button>
          </div>

          {/* New Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Files to Upload:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {uploadedFiles.map((uploadedFile) => (
                  <div
                    key={uploadedFile.id}
                    className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* File Icon */}
                      <div className="flex-shrink-0">
                        {getFileIcon(uploadedFile.type, uploadedFile.file.name)}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {uploadedFile.file.name}
                        </p>
                        <p className="text-sm text-blue-600">
                          {uploadedFile.size} • New upload
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => downloadFile(uploadedFile)}
                        className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFile(uploadedFile.id)}
                        className="p-1 text-red-400 hover:text-red-600 focus:outline-none"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* File Count Info */}
              <div className="mt-2 text-sm text-gray-500">
                {uploadedFiles.length} of {maxFiles} files selected
              </div>
            </div>
          )}
        </div>
      )}
    />
  );
};

export default FileUploadComponent;
